import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Box, IconButton, InputAdornment } from '@mui/material';
import { Form, Formik } from 'formik';
import { React, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { IS_SA } from '../../../config/appConfig';
import { AppRoutes } from '../../../constants/appRoutes';
import { RegionTypes } from '../../../constants/regionType';
import { buildRegionizeRoute } from '../../../helpers/regionRouteHelper';
import { Logout, login } from '../../../services/authService';
import { RegionService } from '../../../services/regionService';
import Logo from '../../../shared/components/common/logo';
import { Toaster } from '../../../shared/components/common/toaster';
import ButtonWrapper from '../../../shared/formUtilities/useButton';
import InputWrapper from '../../../shared/formUtilities/useInput';
import TextWrapper from '../../../shared/formUtilities/useTextWrapper';
import { LoginSchema } from '../../../shared/validations/schema';
import { OTPAuthenticationDialog } from '../components/otpAuthenticationDialog';
import { UserLockOutDialog } from './userLockOutDialog';

function Login() {
	const regionService = new RegionService();

	const navigate = useNavigate();
	const [passwordType, setPasswordType] = useState('password');

	const [loading, setLoading] = useState(false);
	const [isUserLockOut, setIsUserLockOut] = useState(false);
	const [isShowOtpScreen, setIsShowOtpScreen] = useState(false);

	const initialValues = {
		email: '',
		password: '',
	};

	const onSubmit = (values, e) => {
		setLoading(true);
		login(values)
			.then((loginResp) => {
				setLoading(false);
				if (loginResp === 'notAdmin') {
					Toaster({ msg: "Please enter admin's credentials" });
				} else {
					if (loginResp.data?.otpRequested) {
						showOtpVerification(loginResp);
					} else {
						onNavigateToDashboard();
					}
				}
			})
			.catch((err) => {
				checkIfUserLockOut(err);
				setLoading(false);
			});
	};

	const showOtpVerification = () => {
		setIsShowOtpScreen(true);
	};

	const onNavigateToDashboard = () => {
		navigate(`${buildRegionizeRoute(AppRoutes.Dashboard)}`);
	};

	const checkIfUserLockOut = (err) => {
		let response = err?.response || null;
		if (response && response.status === 400 && response?.data?.UserLockedOut) {
			setIsUserLockOut(true);
		}
	};

	return (
		<>
			<div className="flex  flex-col flex-auto items-center justify-center min-w-0 h-full bg-gray">
				<div className="relative flex rounded-2xl shadow-md bg-card bg-white">
					<div className="w-full md:py-8 md:pb-12 md:px-12 p-6 md:max-w-[400px] md:w-[400px] mx-auto sm:mx-0">
						<div className="w-full flex flex-col flex-0 relative">
							<div className="w-24">
								<Logo sx={{ width: 100, height: 'auto' }} />
							</div>
							<TextWrapper
								classList="text-2xl font-bold !text-center !pt-3"
								type="h5"
								text="Sign in"
							/>
							<Box className="text-center mt-6 mb-2">
								<Box className="flex justify-center items-center">
									<Box className="border rounded-md flex items-center p-1 cursor-pointer">
										<Box
											onClick={() => {
												if (loading) {
													return;
												}
												regionService.setRegion(RegionTypes.Global);
											}}
											className={`w-[130px] !text-sm p-1 region-items ${!IS_SA ? 'active-region' : ''
												}`}>
											{'Global'}
										</Box>
										<Box className="w-1"></Box>
										<Box
											onClick={() => {
												if (loading) {
													return;
												}
												regionService.setRegion(RegionTypes.SaudiArabia);
											}}
											className={`w-[130px] !text-sm p-1 region-items ${IS_SA ? 'active-region' : ''
												}`}>
											{'Saudi Arabia'}
										</Box>
									</Box>
								</Box>
							</Box>
						</div>
						<Formik
							initialValues={initialValues}
							onSubmit={onSubmit}
							validationSchema={LoginSchema}
							validateOnMount={true}
							validateOnChange={true}
							enableReinitialize={true}>
							{(formik) => {
								return (
									<Form className="mt-4">
										<Box className="grid grid-rows-2 gap-6">
											<InputWrapper
												key={'email'}
												label="Email"
												size="small"
												error={formik.errors.email && formik.touched.email}
												helperText={
													formik.errors.email && formik.touched.email
														? formik.errors.email
														: ''
												}
												variant="outlined"
												fullWidth={true}
												InputProps={{
													endAdornment: (
														<InputAdornment position="end">
															<IconButton className="!bg-transparent text-primary">
																<EmailOutlinedIcon className="text-md"></EmailOutlinedIcon>
															</IconButton>
														</InputAdornment>
													),
												}}
												type={'email'}
												name="email"></InputWrapper>
											<InputWrapper
												error={
													formik.errors.password && formik.touched.password
												}
												helperText={
													formik.errors.password && formik.touched.password
														? formik.errors.password
														: ''
												}
												fullWidth={true}
												InputProps={{
													endAdornment: (
														<InputAdornment position="end">
															<IconButton
																className="!bg-transparent text-primary"
																onClick={() => {
																	setPasswordType(prevType => (prevType === 'text' ? 'password' : 'text'));
																	const value = formik.values.password;
																	formik.setFieldValue('password', "");
																	setTimeout(() => { formik.setFieldValue('password', value); });
																}}>
																{passwordType === 'password' ? (
																	<VisibilityOffOutlinedIcon></VisibilityOffOutlinedIcon>
																) : (
																	<VisibilityOutlinedIcon></VisibilityOutlinedIcon>
																)}
															</IconButton>
														</InputAdornment>
													),
												}}
												key={'password'}
												label="Password"
												variant="outlined"
												type={passwordType}
												placeholder="Enter Password"
												name="password"
												size="small"
											/>
										</Box>
										<ButtonWrapper
											icon={<LockOpenIcon></LockOpenIcon>}
											loading={loading}
											label="Sign in"
											classList="w-full !mt-8"
											type="submit"></ButtonWrapper>
										<Box className="text-center mt-2">
											<Link
												className="no-underline"
												to={`${buildRegionizeRoute(AppRoutes.ForgotPassword)}`}>
												<span className="caption text-xs !border-b hover:!border-b-0 cursor-pointer">
													Forgot your password?
												</span>
											</Link>
										</Box>
									</Form>
								);
							}}
						</Formik>
					</div>
				</div>
			</div>
			{isUserLockOut ? (
				<UserLockOutDialog
					state={isUserLockOut}
					onClose={() => {
						setIsUserLockOut(false);
					}}></UserLockOutDialog>
			) : null}

			{isShowOtpScreen ? (
				<OTPAuthenticationDialog
					onClose={(isVerified) => {
						setIsShowOtpScreen(false);
						if (isVerified) {
							onNavigateToDashboard();
						} else {
							Logout();
						}
					}}
					state={isShowOtpScreen}></OTPAuthenticationDialog>
			) : null}
		</>
	);
}

export default Login;
